import {
  getAuthToken,
  removeAuthToken,
  setAuthToken,
} from "@asayinc/component-library";
import axios, { AxiosRequestTransformer } from "axios";
import humps from "humps";
import jwt_decode from "jwt-decode";

interface IJWT {
  exp: number;
}

// Buffer time in seconds before token expiry to trigger refresh (5 minutes)
const TOKEN_REFRESH_BUFFER = 300;

// clear local storage and auth token
const logout = () => {
  removeAuthToken();
  window.localStorage.clear();
  window.location.href = window.location.origin;
};

export const refreshTokenIfExpired = () => {
  const token = getAuthToken();
  const refreshToken = window.localStorage.getItem("refresh-token");

  if (token && refreshToken) {
    try {
      const decodedJWT: IJWT = jwt_decode(token);
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const { exp } = decodedJWT;

      // Refresh if token is expired OR will expire within the buffer time
      const shouldRefresh = nowInSeconds >= exp - TOKEN_REFRESH_BUFFER;

      return new Promise((resolve, reject) => {
        if (shouldRefresh) {
          ssoApi
            .post(`authentication/v1/refresh-token/`, {
              refreshToken: refreshToken,
            })
            .then((response) => {
              const { accessToken } = response.data;
              setAuthToken({ value: accessToken });
              resolve(accessToken);
            })
            .catch((error) => {
              // reset to login if refresh fails
              logout();
              reject(error);
            });
        } else {
          resolve(token);
        }
      });
    } catch (error) {
      // If token decode fails, it's invalid
      logout();
      return null;
    }
  }
  logout();
  return null;
};

/**
 * converts my_var to myVar and back for requests/responses.
 * Covers any and all responses/requests. no type needed
 */
const sharedAxiosConfiguration = {
  transformRequest: [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => humps.decamelizeKeys(data),
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
  ],
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosRequestTransformer[]),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => humps.camelizeKeys(data),
  ],
};

// SSO
const ssoApi = axios.create({
  ...sharedAxiosConfiguration,
  baseURL: `${process.env.REACT_APP_AUTHENTICATION_API}/`,
});
